import './app.css';

import { createFakePlayer } from './adapters/audio/fake-player';
import { createMediaSession } from './adapters/audio/media-session';
import { createWebAudioPlayer } from './adapters/audio/web-audio-player';
import { createArtworkLookup } from './adapters/catalog/artwork';
import { createFakeNowPlaying } from './adapters/catalog/fake-nowplaying';
import { createMetadataCascade } from './adapters/catalog/metadata-cascade';
import { createCatalogClient } from './adapters/catalog/radio-browser';
import { createSchemeProbe } from './adapters/catalog/scheme-probe';
import { createIndexedDbHistory } from './adapters/storage/history-db';
import { createWebStorage } from './adapters/storage/web-storage';
import { applyImport, backupFileName, createBackup, parseBackup } from './domain/backup';
import { activeGains, needsAudioGraph } from './domain/eq';
import { findCatalogByUrl, fromCatalog } from './domain/library';
import { createPlayback } from './domain/playback';
import { SEED_STATIONS } from './domain/seed-stations';
import { DEFAULT_SETTINGS, type Settings } from './domain/settings';
import { effectiveUrl, type Station } from './domain/stations';
import { isLang, type Lang, plural, storeLang, translate } from './i18n';
import { must } from './lib/dom';
import { downloadJson, readTextFile } from './lib/download';
import { announce } from './lib/ds';
import { shareLink } from './lib/share';
import { registerServiceWorker } from './lib/sw';
import type { AudioPort } from './ports/audio';
import type { NowPlayingPort } from './ports/metadata';
import {
  type AppState,
  appStore,
  isListedStation,
  type PlayerMode,
  playableStations,
} from './store/app';
import { createJournal } from './store/journal';
import { createLibrary } from './store/library';
import { createMediaMetadataBridge } from './store/media-metadata';
import { createNowPlayingWatcher } from './store/nowplaying';
import { createSearch } from './store/search';
import { createSleepController } from './store/sleep';
import { createBackupDialog } from './ui/components/backup-dialog';
import { createBreadcrumbs } from './ui/components/breadcrumbs';
import { createConfirmDialog } from './ui/components/confirm-dialog';
import { createNowPlaying } from './ui/components/nowplaying';
import { createPlayerFull } from './ui/components/player-full';
import { createStationDialog } from './ui/components/station-dialog';
import { createToastHost } from './ui/components/toast';
import { createVisualizer } from './ui/components/visualizer';
import { VOLUME_STEP } from './ui/components/volume';
import { startKeyboard } from './ui/keyboard';
import { startRouter } from './ui/router';
import { createScreens } from './ui/screens';
import { createTabbar } from './ui/tabbar';

const screenRoot = must<HTMLElement>('#screen');
const screenBody = must<HTMLElement>('#screenBody');
const screenTitle = must<HTMLElement>('#screenTitle');
const crumbsRoot = must<HTMLElement>('#crumbs');
const tabbarRoot = must<HTMLElement>('#tabbar');
const playerRoot = must<HTMLElement>('#player');
const skipLink = must<HTMLAnchorElement>('#skipLink');
const brandLink = must<HTMLAnchorElement>('#brandLink');
const themeToggle = must<HTMLButtonElement>('.theme-toggle');
const addStation = must<HTMLButtonElement>('#addStation');

const params = new URLSearchParams(location.search);

/**
 * Jezik prek ?lang=sl je začasna pot za preverjanje obeh jezikov,
 * dokler nastavitve v fazi 5 ne dobijo pravega preklopa.
 */
const requested = params.get('lang');
if (isLang(requested)) {
  storeLang(requested);
  appStore.set({ lang: requested });
}

/**
 * `?player=fake` samo v razvojnem načinu: E2E tako preveri, da vmesnik sledi
 * stanju porta, brez odvisnosti od tuje postaje in omrežja. V produkcijski
 * gradnji te poti ni — pogoj je statičen, zato jo Vite odstrani.
 */
const useFakePlayer = import.meta.env.DEV && params.get('player') === 'fake';

/**
 * `&volume=off` posnema **iOS**, kjer glasnost ni programsko nastavljiva
 * (`AudioCapabilities.volume`). Brez tega poti glasne odpovedi ne bi bilo
 * mogoče preveriti nikjer razen na iPhonu — in natanko take poti tiho zgnijejo.
 */
const port: AudioPort = useFakePlayer
  ? createFakePlayer(
      params.get('volume') === 'off'
        ? {
            capabilities: {
              eq: true,
              analyser: true,
              outputDeviceSelect: false,
              requiresProxyForEq: true,
              volume: false,
              hlsMse: false,
            },
          }
        : {},
    )
  : createWebAudioPlayer();

/**
 * Ena shramba za vse: knjižnico, dnevnik (čas) in časovnik spanja. Časovnik
 * gre v `sessionStorage`, ostalo v `localStorage` — to loči `web-storage` po
 * ključu, ne klicno mesto.
 */
const storage = createWebStorage();

/**
 * Knjižnica se naloži **asinhrono**, zaslon pa se izriše takoj. Do odgovora
 * shrambe je `libraryReady` `false` in zaslon ve, da seznam še ni znan —
 * kar ni isto kot prazen seznam (prazen je legitimno stanje, T2 §9).
 */
const library = createLibrary({
  storage,
  store: appStore,
  seed: SEED_STATIONS,
});

/**
 * Zgodovina (P2 §4) je v IndexedDB — edini nabor, ki neomejeno raste (T2 §3).
 * Dnevnik nad njo beleži skladbe in sešteva čas poslušanja.
 */
const journal = createJournal({
  store: appStore,
  history: createIndexedDbHistory(),
  storage,
});

/**
 * Seja je imenovana, ker jo rabita dva: krmilnik predvajanja za dejanja in
 * transport, most metapodatkov pa za to, kar sistem kaže. `null`, kadar
 * brskalnik medijske seje nima.
 */
const session = createMediaSession();

/**
 * Iskanje po spletni bazi (faza 4). Ustvarjeno pred krmilnikom predvajanja,
 * ker mu predposluh doda postajo, ki je v knjižnici ni.
 */
const catalog = createCatalogClient();
const search = createSearch({ port: catalog, store: appStore });

/**
 * 🔴 **Pravilo proxyja** (T1 §4) — ena sama funkcija, ki o poti odloči.
 *
 * Sestavljena je iz dveh delov in oba sta nujna:
 *   · uporabnik graf **rabi** (EQ ni raven ali je vizualizator vklopljen),
 *   · platforma ga **zmore** — na iOS `createMediaElementSource()` za žive
 *     tokove ne dela (P2 §6.5), in tok tam ni razloga voditi prek Workerja
 *     za graf, ki ne bo nastal.
 */
function graphNeeded(settings: Settings): boolean {
  if (!port.capabilities.eq && !port.capabilities.analyser) return false;
  return needsAudioGraph(settings);
}

const playback = createPlayback({
  port,
  needsAudioGraph: () => graphNeeded(appStore.get().settings),
  /**
   * Uporabnikov seznam **in** zadetek v predposluhu (P2 §2.4).
   *
   * Predposluh v `stations` ne sme priti — to je uporabnikov seznam in vanj
   * ne gre nič, česar ni dodal sam. Predvajanje pa mora postajo najti po `id`,
   * sicer `play()` tiho ne naredi ničesar.
   */
  stations: () => playableStations(appStore.get()),
  // ⏮ ⏭ gresta po VIDNEM seznamu, ne po celem (P2 §0.4). Predposluha
  // namenoma ni v njem: ⏭ z njega pelje v knjižnico in ne nazaj vanj.
  transportOrder: () => library.visible(),
  session,
  resolveHttps: createSchemeProbe(),
  onResolved: (stationId, url) => {
    library.setResolved(stationId, url);
  },
  onPlayed: (stationId) => {
    // Predposluh ni poslušanje uporabnikove postaje: `playCount`, »nedavno«
    // in `lastStationId` bi dobili `id`, ki ga ob naslednjem zagonu ni.
    // Merilo je članstvo v seznamu, ne `search.preview` — ta se med
    // predvajanjem zamenja ali počisti in id bi za en izris zdrsnil skozi.
    if (!isListedStation(appStore.get(), stationId)) return;
    library.markPlayed(stationId);
  },
  onChange: (snapshot) => {
    appStore.set(snapshot);
  },
});

/**
 * Faktor postopnega utišanja iz časovnika spanja (P2 §5.8).
 *
 * ⚠️ **Živi tu in ne v predvajalniku**, ker se množi z uporabnikovo glasnostjo
 * in ta se od faze 7 med tekom spreminja. Brez skupnega mesta bi drsnik med
 * pojemanjem izhodno glasnost vrnil na polno — časovnik bi bil videti pokvarjen
 * in zvok bi se ob koncu odrezal namesto ugasnil.
 */
let outputScale = 1;

/** Uporabnikova glasnost × faktor pojemanja → predvajalnik. */
function applyVolume(): void {
  const { volume, muted } = appStore.get().settings;
  playback.setVolume(volume * outputScale);
  playback.setMuted(muted);
}

function setVolume(value: number): void {
  library.setVolume(value);
  applyVolume();
}

function toggleMute(): void {
  library.setMuted(!appStore.get().settings.muted);
  applyVolume();
}

/**
 * Korak glasnosti s tipkovnice (`↑` `↓`, P2 §8.8).
 *
 * Korak je isti kot na drsniku (`VOLUME_STEP`), da se odčitek ujema — tipkanje
 * in vlečenje sta dve poti do iste vrednosti in ne dve lestvici.
 *
 * »Glasneje« z utišanega **odpravi tišino**: uporabnik je pravkar povedal, da
 * hoče slišati, in dvig glasnosti pod tišino bi bil tiho neukrepanje.
 */
function stepVolume(direction: 1 | -1): void {
  // Kjer glasnost ni nastavljiva (iOS), bližnjica nima česa premakniti. Razlog
  // je zapisan ob drsniku v Nastavitvah; toast ob vsakem pritisku puščice bi
  // bil nadlega, ne pojasnilo.
  if (!port.capabilities.volume) return;

  const { volume, muted } = appStore.get().settings;
  if (muted && direction === 1) library.setMuted(false);
  setVolume(volume + (VOLUME_STEP / 100) * direction);
}

/**
 * Časovnik spanja (P2 §5). Ob poteku ustavi predvajanje (pavza, kot gumb v
 * aplikaciji), zadnjih 30 s pa množi glasnost s faktorjem pojemanja — nikoli
 * ne povozi uporabnikove glasnosti, le jo pomnoži in po koncu vrne (§5.8).
 */
const sleep = createSleepController({
  store: appStore,
  storage,
  onExpire: () => {
    const state = appStore.get().playback;
    if (state === 'playing' || state === 'loading' || state === 'reconnecting') {
      void playback.toggle();
    }
  },
  setOutputScale: (scale) => {
    outputScale = scale;
    applyVolume();
  },
});

/**
 * Efektivno temo drži `data-theme` prek `ds/js/theme.js`; nastavimo jo z
 * njegovim preklopom (dve stanji, zato preklop doseže cilj) — ta obstojno
 * zapiše ključ in sproži dogodek, ki store in izvozne nastavitve uskladi.
 */
function applyTheme(theme: 'dark' | 'light'): void {
  if (document.documentElement.getAttribute('data-theme') === theme) return;
  const api = window.nesylab?.theme;
  if (api !== undefined) {
    api.toggle();
    return;
  }
  document.documentElement.setAttribute('data-theme', theme);
  syncTheme();
}

/** Uskladi store in izvozne nastavitve z dejansko temo (`data-theme`). */
function syncTheme(): void {
  const theme = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  if (appStore.get().theme === theme) return;
  appStore.set({ theme });
  library.setTheme(theme);
}

// Preklop iz glave (`.theme-toggle`) sproži ta dogodek; poslušalec uskladi
// store, da mu zaslon Nastavitve sledi tudi, kadar ni bil vir spremembe.
window.addEventListener('nesylab:theme', syncTheme);

function setLang(lang: Lang): void {
  storeLang(lang);
  appStore.set({ lang });
  library.setLang(lang);
}

/**
 * Koliko časa mora biti drsnik pri miru, preden se pot toka uskladi.
 *
 * ⚠️ **Ni kozmetika.** Vlečenje s +5 na −5 gre skozi 0 dB in tam za hip
 * ustreza »graf ni potreben«. Brez zamika bi se tok med enim samim potegom
 * dvakrat prekinil in znova medpomnil. `syncRoute()` je sicer idempotenten in
 * bi drugo prekinitev preprečil, prve pa ne.
 */
const ROUTE_SETTLE_MS = 400;
let routeSettleTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Nastavitve zvoka → predvajalnik (faza 6).
 *
 * Dvoje, in vsako ob svojem času:
 *   1. **Ojačitve gredo v graf takoj.** Klic je poceni in filtri se odzovejo
 *      med predvajanjem, brez prekinitve — drsnik se sliši sproti.
 *   2. **Pot se uskladi, ko se drsnik umiri.** Sprememba poti pomeni nov `src`
 *      in s tem prekinitev zvoka; `syncRoute()` jo naredi samo, kadar se
 *      zahtevani graf res razlikuje od naloženega.
 */
function applyAudioSettings(): void {
  playback.setEq(activeGains(appStore.get().settings));

  clearTimeout(routeSettleTimer);
  routeSettleTimer = setTimeout(() => {
    void playback.syncRoute();
  }, ROUTE_SETTLE_MS);
}

/**
 * Modala živita nad zaslonom in ne v njem: `.cm-scrim` je `position: fixed`
 * čez ves viewport in bi ga zaslon, ki se ob menjavi zavihka skrije,
 * skril skupaj s seboj.
 */
const dialog = createStationDialog(document.body, {
  onSubmit: (input, editingId) => {
    if (input.name.trim() === '') return 'name';
    if (!/^https?:\/\/.+/i.test(input.url.trim())) return 'url';

    if (editingId !== null) {
      library.editStation(editingId, {
        name: input.name,
        url: input.url,
        tags: input.tags ?? [],
        ...(input.countryCode === undefined ? {} : { countryCode: input.countryCode }),
      });
      return null;
    }

    // Podvajanje ve le knjižnica — primerja po naslovu, tudi razrešenem.
    return library.addStation(input) === null ? 'duplicate' : null;
  },
  onDelete: (stationId) => {
    // Brisanje TEKOČE postaje najprej ustavi predvajanje: sicer zvok teče
    // naprej z visečim `stationId`, ki kaže na postajo, ki je ni več — vrstica
    // javi »izberite postajo«, ⏮ ⏭ padeta v vejo »ni v seznamu«, kaskada pa
    // poizveduje za izbrisano postajo. `stop()` počisti `stationId` in kartico.
    if (appStore.get().stationId === stationId) playback.stop();
    library.deleteStation(stationId);
  },
  /**
   * Samodejno dopolnjevanje (P2 §1.12). Vir išče po imenu (`catalog.search`),
   * ujamemo pa po **naslovu**: med postajami s podobnim imenom je naslov tisti,
   * ki pove, katera je res ista (T2 §7, točka 5). Branje sme skozi Worker in
   * KV — po IP se šteje le klik (`reportClick`), zato ta pot ne izniči vira.
   */
  lookup: async (name, url) => {
    const result = await catalog.search({ q: name, countryCode: null, tag: null });
    if (!result.ok) return null;
    return findCatalogByUrl(result.stations, url);
  },
});

function stationById(stationId: string): Station | null {
  return appStore.get().stations.find((s) => s.id === stationId) ?? null;
}

const backupDialog = createBackupDialog(document.body, {
  stations: () => appStore.get().stations,
  onImport: (backup, mode) => {
    const merged = applyImport(appStore.get().stations, backup, {
      mode,
      newId: () => crypto.randomUUID(),
    });
    // Nastavitve prevzamemo samo, kadar so v datoteki — »samo postaje«
    // jih namenoma nima in bi jih privzete tiho povozile.
    library.replaceAll(merged, backup.data.settings);

    // Tema in jezik iz kopije se tudi UVELJAVITA, ne le shranita — sicer bi
    // celotna obnovitev pustila napačen videz do naslednjega ročnega preklopa.
    const imported = backup.data.settings;
    if (imported !== undefined) {
      setLang(imported.lang);
      applyTheme(imported.theme);
      // Enako velja za EQ: kopija ga lahko prinese vklopljenega z lastno
      // krivuljo. Brez tega bi obstal v nastavitvah, dokler se uporabnik ne
      // dotakne drsnika — videti kot uvoz, ki polovico ni prijel.
      applyAudioSettings();
    }

    // Celotna obnovitev zgodovine in časa velja pri ZAMENJAVI (selitev na novo
    // napravo). Pri združevanju uporabnikovih obstoječih ne mešamo — seštevanje
    // časa poslušanja z dveh naprav ni pomen, ki bi ga uporabnik pričakoval.
    if (
      mode === 'replace' &&
      (backup.data.stats !== undefined || backup.data.history !== undefined)
    ) {
      void journal.restore(backup.data.stats ?? [], backup.data.history ?? []);
    }
  },
  onClearSeed: () => {
    library.clearSeed();
  },
});

/**
 * Potrditev nepovratnega brisanja zgodovine (P2 §4.7). Isti modal potrjuje
 * »počisti vse« in »počisti eno postajo«; besedilo se poda ob odpiranju.
 */
const confirm = createConfirmDialog(document.body);

/**
 * Toasti živijo v `<body>` iz istega razloga kot modali: `.toast-wrap` je
 * `position: fixed` čez viewport in bi ga zaslon ob menjavi zavihka skril
 * skupaj s seboj.
 */
const toasts = createToastHost(document.body);

// Pot je krom, ne vsebina: živi v glavi in ne drsi z zaslonom. Naslov, ki mu
// pripada, pa mora ostati v `<main>` — zato dva korena (glej breadcrumbs.ts).
const crumbs = createBreadcrumbs(crumbsRoot, screenTitle);

const screens = createScreens(screenBody, {
  onPlay: (stationId) => {
    void playback.play(stationId);
  },
  onQuery: (query) => {
    library.setQuery(query);
  },
  onSortMode: (mode) => {
    library.setSortMode(mode);
  },
  onFavouritesOnly: (on) => {
    library.setFavoritesOnly(on);
  },
  onToggleFavourite: (stationId) => {
    library.toggleFavorite(stationId);
  },
  onMove: (stationId, step) => {
    library.move(stationId, step);
  },
  /**
   * ⚠️ **Dno je `MAX_SAFE_INTEGER`, ne izmerjena dolžina.**
   *
   * `reorderStation()` cilj sam omeji na `[0, length - 1]`, zato je vsako
   * dovolj veliko število »na konec« — in pri tem ni tekmovanja z izrisom.
   * Dolžina, prebrana tu, bi bila posnetek: če bi se seznam med klikom in
   * commitom spremenil, bi postaja pristala eno mesto od dna.
   */
  onMoveEdge: (stationId, edge) => {
    library.reorder(stationId, edge === 'top' ? 0 : Number.MAX_SAFE_INTEGER);
  },
  onReorder: (stationId, toIndex) => {
    library.reorder(stationId, toIndex);
  },

  /**
   * Deljenje postaje (P2 §3.13).
   *
   * Deli se **naslov toka**, ne povezava na aplikacijo: prejemnik ga odpre v
   * čemer koli. Ko gre pot na odložišče (Firefox, nezavarovan kontekst), mora
   * to izvedeti — sicer je videti, kot da gumb ni naredil ničesar. Uspešno
   * sistemsko deljenje in preklic pa ostaneta tiha: prvo je vidno samo po
   * sebi, drugo je uporabnikova odločitev.
   */
  onShare: (stationId) => {
    const station = stationById(stationId);
    if (station === null) return;
    const { lang } = appStore.get();

    void shareLink({
      title: station.name,
      text: station.name,
      url: effectiveUrl(station),
    }).then((outcome) => {
      if (outcome === 'copied') announce(translate(lang, 'station.share.copied'));
    });
  },
  onAdd: () => {
    dialog.openAdd();
  },
  onEdit: (stationId) => {
    const station = stationById(stationId);
    if (station !== null) dialog.openEdit(station);
  },
  // Brisanje je nepovratno in zato nikoli en klik (P2 §4.7).
  onDelete: (stationId) => {
    const station = stationById(stationId);
    if (station !== null) dialog.confirmDelete(station);
  },

  onExport: (kind) => {
    const state = appStore.get();
    const now = Date.now();
    downloadJson(
      backupFileName(kind, now),
      createBackup({
        kind,
        stations: state.stations,
        settings: state.settings,
        // Celotna kopija je varovalka in selitev: zgodovina in čas gresta
        // zraven, `createBackup` ju pri »samo postaje« spusti (T2 §7).
        stats: state.stats,
        history: state.history,
        now,
        appVersion: __APP_VERSION__,
        buildSha: __BUILD_SHA__,
      }),
    );
    // Prenos je na namizju viden, na mobilnem PWA pogosto ne — toast potrdi.
    // ⚠️ Če uporabnik prekliče sistemsko shranjevanje, tega ne izvemo; na
    // večini brskalnikov gre datoteka v Prenose brez dialoga, zato sprejemljivo.
    toasts.show(
      'success',
      translate(state.lang, 'settings.export.toast.title'),
      translate(
        state.lang,
        kind === 'full' ? 'settings.export.toast.full' : 'settings.export.toast.stations',
      ),
      translate(state.lang, 'dialog.close'),
    );
  },

  onImportFile: (file) => {
    void readTextFile(file).then((text) => {
      if (text === null) {
        backupDialog.showFailure({ reason: 'unreadable' });
        return;
      }
      const result = parseBackup(text);
      // Predogled pride PRED zapisom — uvoz brez njega je nepovraten poseg
      // v edine podatke, ki jih uporabnik ima (T2 §7, točka 3).
      if (result.ok) backupDialog.openImport(result.backup);
      else backupDialog.showFailure(result);
    });
  },

  onClearSeed: () => {
    backupDialog.confirmClearSeed();
  },

  // Seed se naloži samo ob prvem zagonu (T2 §9), zato nove prednaložene
  // postaje sicer ne dosežejo nikogar, ki je aplikacijo že odprl. Dejanje je
  // izrecno: dodajanje je popravljivo, tiho prilivanje ob nadgradnji pa bi
  // vračalo postaje, ki jih je uporabnik namerno izbrisal.
  missingSeedCount: () => library.missingSeedCount(),
  onRestoreSeed: () => {
    // `restoreSeed()` vrne, koliko jih je res dodalo. Izid pristane na zaslonu
    // Postaje, ne tu, zato ga toast potrdi — sicer se dejanje zdi brez učinka.
    const added = library.restoreSeed();
    if (added === 0) return;
    const { lang } = appStore.get();
    toasts.show(
      'success',
      translate(lang, 'settings.seed.restore.toast.title'),
      plural(lang, 'settings.seed.restore.toast', added),
      translate(lang, 'dialog.close'),
    );
  },

  // Počisti vse podatke (P2 §6.16) — najbolj rušilno dejanje. Nikoli en klik:
  // rdeč gumb pelje skozi potrditev (`confirm` je cancel-focused).
  onReset: () => {
    confirm.open({
      titleKey: 'settings.reset.confirm.title',
      bodyKey: 'settings.reset.confirm.body',
      actionKey: 'settings.reset.action',
      onConfirm: () => {
        const { settings, lang } = appStore.get();
        // Vse na privzeto, a tema in jezik ostaneta: gonita ju `data-theme` in
        // `store.lang`, prepis na privzeto bi ju ob naslednjem zagonu vrgel na
        // temno/angleščino. Postaje se izpraznijo, seed se ne vrne sam (ključ
        // v shrambi obstaja), a `seedDismissed: false` dovoli »Dodaj« nazaj.
        library.replaceAll([], { ...DEFAULT_SETTINGS, theme: settings.theme, lang: settings.lang });
        void journal.clearAll();
        toasts.show(
          'success',
          translate(lang, 'settings.reset.done.title'),
          translate(lang, 'settings.reset.done.body'),
          translate(lang, 'dialog.close'),
        );
      },
    });
  },

  /* ── Iskanje po spletni bazi (P2 §2) ──────────────────────────── */

  onSearchQuery: (query) => {
    search.setQuery(query);
  },
  onSearchCountry: (code) => {
    search.setCountry(code);
  },
  onSearchTag: (tag) => {
    search.setTag(tag);
  },
  onSearchRetry: () => {
    search.retry();
  },
  onSearchMore: () => {
    search.more();
  },

  /**
   * Predposluh (P2 §2.4): postaja se zgradi, zaigra in **ne** gre v seznam.
   *
   * Klik se javi viru ob predvajanju in ne ob dodajanju: tako skupnost meri,
   * kaj se posluša, in to je tudi trenutek, ko uporabnik postajo res izbere
   * (P2 §2.8). Javljanje ne sme stati pred zvokom, zato je za `play()`.
   */
  onPreview: (station) => {
    const preview = search.preview(station);
    void playback.play(preview.id);
    catalog.reportClick(station.uuid);
  },

  onAddFound: (station) => {
    library.addStation(fromCatalog(station));
  },

  /* ── Zgodovina (P2 §4) ─────────────────────────────────────────── */

  // Živa ura: skupni čas s tekočim odsekom vred; dnevnik ga izračuna.
  listenMs: (stationId, at) => journal.listenMs(stationId, at),
  onCopyTitle: (text) => {
    // Branje sme spodleteti (dovoljenja, nezavarovan kontekst): tiho, brez
    // sporočila o napaki. Ob uspehu bralnik zaslona naznani, da je kopirano.
    void navigator.clipboard
      ?.writeText(text)
      .then(() => announce(translate(appStore.get().lang, 'history.copied')))
      .catch(() => {});
  },
  onClearAll: () => {
    confirm.open({
      titleKey: 'history.clearAll.confirm.title',
      bodyKey: 'history.clearAll.confirm.body',
      actionKey: 'history.clearAll',
      onConfirm: () => {
        void journal.clearAll();
      },
    });
  },
  onClearStation: (stationId) => {
    confirm.open({
      titleKey: 'history.clearStation.confirm.title',
      bodyKey: 'history.clearStation.confirm.body',
      actionKey: 'history.clearStation.action',
      onConfirm: () => {
        void journal.clearStation(stationId);
      },
    });
  },

  /* ── Časovnik spanja (P2 §5) ───────────────────────────────────── */

  onStart: (durationMs) => {
    sleep.start(durationMs);
  },
  onCancel: () => {
    sleep.cancel();
  },
  onNudge: (deltaMs) => {
    sleep.nudge(deltaMs);
  },

  /* ── Nastavitve (P2 §6) ────────────────────────────────────────── */

  onSetTheme: applyTheme,
  onSetLang: setLang,

  /**
   * ⛔ **Glasnosti med odvisnostmi zaslonov NI** in to ni pozaba.
   *
   * Vrstica glasnosti je iz Nastavitev odpadla z B-5 (2026-07-22): razširjen
   * pogled je isto pot ponudil v enakem številu dotikov. Kontrolnik zdaj živi
   * samo v `nowplaying` (od 720px) in v `player-full` — oba dobita `onVolume`,
   * `onMuteToggle` in `volumeSupported` po svoji poti, nižje v tej datoteki.
   */

  /* ── Zvok: ekvalizer in vizualizator (P2 §6.3–6.5) ─────────────── */

  onEqEnabled: (on) => {
    library.setEqEnabled(on);
    applyAudioSettings();
  },
  onEqPreset: (preset) => {
    library.setEqPreset(preset);
    applyAudioSettings();
  },
  onEqGain: (index, db) => {
    const gains = [...appStore.get().settings.eqGains];
    gains[index] = db;
    library.setEqGains(gains);
    applyAudioSettings();
  },
  onVisualizerEnabled: (on) => {
    library.setVisualizerEnabled(on);
    applyAudioSettings();
  },
  audioGraphSupported: () => port.capabilities.eq && port.capabilities.analyser,
  // Glasna odpoved, ne tiha (P2 §6.5): uporabnik, ki premakne drsnike in ne
  // sliši razlike, išče napako v svojih slušalkah.
  onUnsupported: () => {
    const { lang } = appStore.get();
    toasts.show(
      'info',
      translate(lang, 'settings.eq.unsupported.title'),
      translate(lang, 'settings.eq.unsupported.body'),
      translate(lang, 'dialog.close'),
    );
  },

  appVersion: __APP_VERSION__,
  buildSha: __BUILD_SHA__,
});

const tabbar = createTabbar(tabbarRoot);

const nowPlaying = createNowPlaying(playerRoot, {
  onToggle: () => {
    void playback.toggle();
  },
  onPrevious: () => {
    void playback.previous();
  },
  onNext: () => {
    void playback.next();
  },
  onVolume: setVolume,
  onMuteToggle: toggleMute,
  volumeSupported: () => port.capabilities.volume,
  onExpand: () => {
    setPlayerMode('full');
  },
});

/**
 * Razširjen pogled in način za avto (P2 §3.11, §7).
 *
 * Živita v `<body>` in ne v zaslonu — enako kot modali: `.nowplaying-full` je
 * `position: fixed` čez ves viewport in bi ga zaslon ob menjavi zavihka skril
 * skupaj s seboj.
 */
const playerFull = createPlayerFull(document.body, {
  onToggle: () => {
    void playback.toggle();
  },
  onPrevious: () => {
    void playback.previous();
  },
  onNext: () => {
    void playback.next();
  },
  onVolume: setVolume,
  onMuteToggle: toggleMute,
  volumeSupported: () => port.capabilities.volume,
  onClose: () => {
    closePlayer();
  },
  onCarMode: (on) => {
    setPlayerMode(on ? 'car' : 'full');
  },
});
// Vidnost vrstice odslej vodi `syncChrome()`: ob odprtem celozaslonskem
// pogledu mora biti skrita (glej tam — pogoj prehoda View Transitions).

/**
 * Vizualizator leži **za** vsebino medijske vrstice (D2 §11): je okras, ne
 * podatek, in ničesar ne sme prekriti.
 *
 * Lastna komponenta z lastnim poddrevesom, ne del `nowplaying.ts` — vsaka
 * komponenta ima svoje vozlišče in nobena ne popravlja tujega (T1 §5).
 */
const visualizer = createVisualizer(playerRoot, {
  getAnalyser: () => port.getAnalyser(),
});

/**
 * Kaskada metapodatkov teče ločeno od zvoka: vir naslova skladbe ni vir toka
 * (T3 §3).
 *
 * S ponarejenim predvajalnikom se ne zažene. `?player=fake` obstaja, da E2E
 * ne kliče tujih strežnikov — kaskada bi to obšla in test bi bil odvisen od
 * tega, kaj postaja v tistem trenutku igra.
 */
/**
 * ⚠️ **Lažna skladba je izrecna (`&track=1`), ne privzeta.**
 *
 * Brez naslova skladbe je `track` `null` — in to je **pogosto, legitimno
 * stanje** (govorne postaje, marsikateri Icecast), ki ga preverja večina
 * obstoječih testov: vrstica takrat kaže ime postaje. Ko je bil lažni vir
 * privzet, jih je osem naenkrat padlo, ker so v naslovu našli skladbo.
 */
function metadataPort(): NowPlayingPort {
  if (!useFakePlayer) return createMetadataCascade();
  return params.get('track') === '1' ? createFakeNowPlaying() : { watch: () => () => undefined };
}

const nowPlayingWatcher = createNowPlayingWatcher({
  port: metadataPort(),
  store: appStore,
  onCapability: (stationId, capability) => {
    library.setMetaCapability(stationId, capability);
  },
  lookupArtwork: createArtworkLookup(),
});

/**
 * Skladba in ovitek iz store v medijsko sejo.
 *
 * `startPlayback` sistemu ime postaje postavi **takoj**, še v nalogi geste —
 * to ostane, ker iOS sejo ustvari ob predvajanju in zamuda tam stane mesto
 * Now Playing. Most jo od tam naprej samo obogati.
 */
const mediaMetadata = createMediaMetadataBridge({ session });

/* ── Pogled predvajalnika (P2 §3.11, §7) ──────────────────────────── */

/**
 * Prehod vrstica ↔ celozaslon (P2 §3.11).
 *
 * `startViewTransition()` posname staro sliko, **sinhrono** požene povratni
 * klic in posname novo. Naš `set()` izriše takoj, zato je vse, kar mora ta
 * funkcija narediti, da izris zavije vanj — imeni (`nowplaying-art`,
 * `nowplaying-title`) sta v DS in ju aplikacija ne postavlja.
 *
 * ⚠️ **Brez podpore (Firefox pred 144) se pogled vseeno odpre.** Prehod je
 * okras; odsotnost okrasa ne sme pomeniti odsotnosti funkcije.
 */
function renderPlayerMode(mode: PlayerMode): void {
  const apply = (): void => {
    appStore.set({ player: mode });
  };
  const start = document.startViewTransition?.bind(document);
  if (start === undefined) {
    apply();
    return;
  }
  start(apply);
}

/**
 * ⚠️ **Odpiranje potisne vnos v zgodovino, zapiranje gre nazaj po njej.**
 *
 * Brez tega bi gumb nazaj (in poteg nazaj na Androidu) iz odprtega
 * celozaslonskega pogleda **zapustil aplikacijo** — v nameščeni PWA pomeni to
 * prekinjen zvok. Vnos nima svojega hasha: pot nosi zavihek, predvajalnik pa
 * je plast nad njim in ne zaslon (D3 #10).
 *
 * Preklop `full` → `car` novega vnosa **ne** doda: nazaj mora zapreti pogled,
 * ne vrniti iz avtomobilskega v razširjenega. Način za avto je ista plast.
 */
function setPlayerMode(mode: PlayerMode): void {
  const wasOpen = appStore.get().player !== 'bar';
  renderPlayerMode(mode);
  if (!wasOpen && mode !== 'bar') history.pushState({ nwPlayer: true }, '');
}

function closePlayer(): void {
  if (appStore.get().player === 'bar') return;
  // Skozi zgodovino, da gumb »zapri« in gumb nazaj naredita isto — sicer bi za
  // vsakim zaprtjem ostal odvečen vnos in nazaj bi pogled znova odprl.
  history.back();
}

window.addEventListener('popstate', () => {
  if (appStore.get().player !== 'bar') renderPlayerMode('bar');
});

/**
 * Skok na vsebino ne sme skozi router: #screen ni zavihek in bi
 * z zapisom v hash pomenil menjavo zaslona.
 */
skipLink.addEventListener('click', (event) => {
  event.preventDefault();
  screenRoot.focus();
});

/**
 * Primarno dejanje zaslona Postaje stoji v topbaru, ne v orodni vrstici:
 * ta je na 390px zrasla na tri vrstice. Skrije se na drugih zavihkih, ker
 * bi drugje ne pomenilo ničesar.
 */
addStation.addEventListener('click', () => {
  dialog.openAdd();
});

function syncChrome(): void {
  const { lang, tab, player } = appStore.get();
  document.documentElement.lang = lang;

  /**
   * ⛔ **Vrstica se ob odprtem celozaslonskem pogledu SKRIJE, in to je pogoj
   * prehoda, ne kozmetika.**
   *
   * `view-transition-name` mora biti v dokumentu enkraten. Naslovnica vrstice
   * in naslovnica celega zaslona nosita isto ime (`nowplaying-art`, iz DS);
   * če bi bili hkrati vidni, bi se prehod tiho odpovedal. Element z
   * `display: none` v zajem ne gre — `[hidden]` to doseže (D2 §18).
   */
  playerRoot.toggleAttribute('hidden', player !== 'bar');
  // Kateri zaslon je na vrsti, je stanje lupine: `#screen` je `<main>`, ki ga
  // tovarna zaslonov ne vidi več (piše v `#screenBody`).
  screenRoot.dataset.screen = tab;
  dialog.setLang(lang);
  backupDialog.setLang(lang);
  confirm.setLang(lang);

  addStation.toggleAttribute('hidden', tab !== 'stations');
  addStation.setAttribute('aria-label', translate(lang, 'stations.add'));
  addStation.title = translate(lang, 'stations.add');
  skipLink.textContent = translate(lang, 'a11y.skipToContent');
  // Logotip je povezava na nesylab.com. Napis „nesylab" je znamka in ostane
  // neprevedljiv; prevede se oznaka, ker mora povedati, da se odpre drugje.
  brandLink.setAttribute('aria-label', translate(lang, 'a11y.brandHome'));
  // ds/js/theme.js postavi angleški aria-label ob DOMContentLoaded; prepišemo ga za njim.
  themeToggle.setAttribute('aria-label', translate(lang, 'a11y.toggleTheme'));
}

/**
 * En sam seznam izrisovalcev. Prej sta bila dva — telo naročnika in ročni
 * prvi izris — in nova komponenta je zahtevala popravek na obeh mestih;
 * kdor bi jo dodal le v enega, bi dobil zaslon, ki se pojavi šele ob prvi
 * spremembi stanja. Napaka, ki se vidi samo ob hladnem zagonu.
 */
function render(state: AppState): void {
  nowPlayingWatcher.sync(state);

  /**
   * ⛔ Vse pod watcherjem bere SVEŽE stanje, ne parametra `state`.
   *
   * `nowPlayingWatcher.sync()` ob menjavi postaje sinhrono zapiše v store
   * (`track: null, artworkUrl: null`). Zapis sproži naročnike, torej **ugnezden
   * `render()`**, ki steče do konca — in ko se vrne, bi zunanji klic nadaljeval
   * s parametrom, ki je medtem zastarel, ter sveži izris povozil s starim.
   *
   * Na medijski seji se je to videlo kot skladba prejšnje postaje pod imenom
   * nove; v vmesniku kot ovitek, ki se ob menjavi ne umakne.
   */
  const fresh = appStore.get();

  // Dnevnik in časovnik berta isto sveže stanje: dnevnik zabeleži skladbo in
  // šteje čas, časovnik opazuje predvajanje za samodejni preklic. Oba PO
  // watcherju, ki je pravkar morda postavil ali počistil `track`.
  journal.sync(fresh);
  sleep.sync(fresh);

  // Izbire filtrov se naložijo ob prvem obisku zavihka in nikoli prej: 241
  // držav in 150 žanrov je dva zahtevka, ki ju kdor iskanja ne odpre, ne rabi.
  if (fresh.tab === 'search') search.loadFacets();

  mediaMetadata.sync(fresh);
  crumbs.update(fresh);
  screens.update(fresh);
  tabbar.update(fresh);
  nowPlaying.update(fresh);
  visualizer.update(fresh);
  playerFull.update(fresh);
  syncChrome();
}

appStore.subscribe(render);

// Prvi izris PRED usmerjevalnikom: `startRouter()` prebere hash in postavi
// zavihek, kar naročnika sproži samo, če se je stanje res spremenilo. Tako
// je izris zagotovljen natanko enkrat, ne dvakrat kot doslej.
render(appStore.get());

startRouter();

/**
 * Tipkovnične bližnjice (P2 §8.8). Seznam je viden v Nastavitvah — bližnjica,
 * ki je nikjer ne piše, je funkcija samo za tistega, ki jo že pozna.
 */
startKeyboard({
  onToggle: () => {
    void playback.toggle();
  },
  onPrevious: () => {
    void playback.previous();
  },
  onNext: () => {
    void playback.next();
  },
  onVolumeStep: stepVolume,
  onMuteToggle: toggleMute,
});

registerServiceWorker();

/**
 * Bližnjice iz manifesta (P2 §8.7).
 *
 * ⚠️ **`?play=last` je edina pot, po kateri aplikacija zaigra brez dotika** —
 * in to ni kršitev WCAG 1.4.2, ampak njeno nasprotje: uporabnik je izbral
 * bližnjico, ki se **imenuje** »zadnja postaja«. Navaden zagon ne predvaja
 * nikoli. Če brskalnik zahteva gesto, `play()` zavrne z `blocked` in vrstica
 * pove »za začetek pritisnite gumb« — poštena odpoved, ne tišina.
 *
 * Obojemu je pogoj naložena knjižnica: pred njo `lastStationId` ni znan in
 * filter priljubljenih nima česa filtrirati.
 */
function applyLaunchIntent(): void {
  if (params.get('favourites') === '1') library.setFavoritesOnly(true);

  if (params.get('play') !== 'last') return;
  const last = appStore.get().settings.lastStationId;
  // Prvi zagon po namestitvi: bližnjica obstaja, zadnja postaja pa še ne.
  if (last === undefined) return;
  void playback.play(last);
}

/**
 * Ojačitve in glasnost gredo v predvajalnik **takoj po branju shrambe**, ne
 * šele ob prvem premiku drsnika. Brez tega bi shranjena krivulja obstala v
 * nastavitvah in prvo predvajanje bi teklo ravno — pot bi bila prava (prek
 * Workerja), zvok pa nespremenjen. Natanko tista vrsta neujemanja, ki je
 * videti kot pokvarjen EQ. Enako velja za shranjeno glasnost in tišino.
 */
void library.init().then(() => {
  playback.setEq(activeGains(appStore.get().settings));
  applyVolume();
  applyLaunchIntent();
});
void journal.init();
sleep.init();

/**
 * Zapiši tekoči odsek poslušanja, preden zavihek izgine.
 *
 * `pagehide` je zanesljivejši od `beforeunload` na mobilnem (ta se ob prehodu
 * v ozadje pogosto ne sproži). Brez tega bi se čas zadnjega, še ne zapisanega
 * odseka izgubil ob zaprtju — dnevnik commita šele ob koncu odseka.
 */
window.addEventListener('pagehide', () => {
  journal.flush();
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') journal.flush();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncChrome);
}
